import { generateRandomString, getHeaders } from "./common";
import signalRService from "js/services/SignalRService";

// Initiate a group call for a policy
export const initiateGroupCall = async (policyId, callerName) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_POLICY_API_URL
            }/group_call/${policyId}?callerName=${encodeURIComponent(callerName)}`,
            {
                method: "POST",
                headers: getHeaders(),
                credentials: "include",
            }
        );

        let callerId = null;
        if (response.ok) {
            try {
                const data = await response.json();

                callerId = data.callerId;
            } catch (e) {
                console.log("No JSON response from call_devices API");
            }
        }

        return {
            success: response.ok,
            error: !response.ok
                ? `Group call request failed with status ${response.status}`
                : null,
            callerId: callerId,
        };
    } catch (error) {
        console.error("Error initiating group call:", error);
        return { success: false, error: error.message };
    }
};

// Initiate an event call
export const initiateEventCall = async (eventId, callerName) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_EVENT_API_URL
            }/group_call/${eventId}?callerName=${encodeURIComponent(callerName)}`,
            {
                method: "POST",
                headers: getHeaders(),
                credentials: "include",
            }
        );

        let callerId = null;
        if (response.ok) {
            try {
                const data = await response.json();

                callerId = data.callerId;
            } catch (e) {
                console.log("No JSON response from call_devices API");
            }
        }

        return {
            success: response.ok,
            error: !response.ok
                ? `Event call request failed with status ${response.status}`
                : null,
            callerId: callerId,
        };
    } catch (error) {
        console.error("Error initiating event call:", error);
        return { success: false, error: error.message };
    }
};

export const callDevice = async (deviceId, callerName, userId = null, call_id = null) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL
            }/call_devices/${deviceId}?CallerName=${encodeURIComponent(callerName)}${userId ? `&UserId=${userId}` : ""}${call_id ? `&call_id=${call_id}` : ""}`,
            {
                method: "GET",
                headers: getHeaders(),
                credentials: "include",
            }
        );
        let callerId = null;
        if (response.ok) {
            try {
                const data = await response.json();
                callerId = data.callerID;
            } catch (e) {
                console.log("No JSON response from call_devices API");
            }
        }

        return {
            success: response.ok,
            error: !response.ok
                ? `Device call request failed with status ${response.status}`
                : null,
            callerId: callerId,
        };
    } catch (error) {
        console.error("Error calling device:", error);
        return { success: false, error: error.message };
    }
};

// Get JWT token for Agora based on call type
export const getCallToken = async (callType, id) => {
    try {
        let tokenEndpoint;

        switch (callType) {
            case "event":
                tokenEndpoint = `${process.env.REACT_APP_EVENT_API_URL}/group_jwt/${id}`;
                break;
            case "group":
                tokenEndpoint = `${process.env.REACT_APP_POLICY_API_URL}/group_jwt/${id}`;
                break;
            case "device":
                tokenEndpoint = `${process.env.REACT_APP_DEVICE_API_URL}/jwt/${id}`;
                break;
            default:
                throw new Error("Invalid call type provided");
        }

        const response = await fetch(tokenEndpoint, {
            method: "GET",
            headers: getHeaders(),
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error(`Token request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (!data.token) {
            throw new Error("No token received from server");
        }

        return { success: true, token: data.token };
    } catch (error) {
        console.error("Error getting call token:", error);
        return { success: false, error: error.message };
    }
};

export const connectSignalR = async (
    callerId,
    userId,
    isInitiator = true,
    extUserName = null,
    extUserAgoraUid = null
) => {
    try {
        const connected = await signalRService.connect(
            callerId,
            userId,
            isInitiator,
            extUserName,
            extUserAgoraUid
        );

        if (!connected) {
            return { success: false, error: "Failed to connect to SignalR" };
        }
        return { success: true };
    } catch (error) {
        console.error("Error connecting to SignalR:", error);
        return { success: false, error: error.message };
    }
};

export const fetchActiveUsers = async (
    callerId,
    userId = null,
    deviceId = null
) => {
    if (!callerId) {
        return { success: false, error: "callerId is required" };
    }

    const qs = new URLSearchParams({ caller_id: callerId });
    if (userId) qs.append("user_id", userId);
    if (deviceId) qs.append("device_id", deviceId);

    try {
        const res = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL}/portal_call_status?${qs}`,
            {
                method: "GET",
                headers: getHeaders(),
                credentials: "include",
            }
        );

        if (!res.ok) {
            return {
                success: false,
                error: `portal_call_status failed (${res.status})`,
            };
        }

        const json = await res.json();
        return { success: true, data: json };
    } catch (err) {
        console.error("fetchActiveUsers error:", err);
        return { success: false, error: err.message };
    }
};

export const startCallStatusCheck = (callerId, userId, deviceId, callback) => {
    signalRService.startStatusCheck(callerId, userId, deviceId, (isActive) => {
        if (callback && typeof callback === "function") {
            callback(isActive);
        }
    });
};

export const disconnectSignalR = async () => {
    try {
        await signalRService.disconnect();
        return { success: true };
    } catch (error) {
        console.error("Error disconnecting SignalR:", error);
        return { success: false, error: error.message };
    }
};

// Find Agora RTM token API call
export const getAgoraRtmToken = async (deviceId) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL}/jwt_ey/${deviceId}`,
            {
                method: "GET",
                headers: getHeaders(),
                credentials: "include",
            }
        );

        if (!response.ok) {
            throw new Error(`Token request failed with status ${response.status}`);
        }

        const data = await response.json();
        console.log("Token data: -----------> ", data);

        if (!data.token) {
            throw new Error("No token received from server");
        }

        return { success: true, token: data.token };
    } catch (error) {
        console.error("Error getting call token:", error);
        return { success: false, error: error.message };
    }
};

// Start transcription API call
export const startTranscription = async (device1, userId, device_lang, user_lang) => {
    try {

        const payload = { device_1: device1, user_id: userId }

        // Add device_lang and user_lang if provided
        if (device_lang) {
            payload.device_lang = device_lang;
        }
        if (user_lang) {
            payload.user_lang = user_lang;
        }

        const response = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL}/starttranscription_ey`,
            {
                method: "POST",
                headers: getHeaders(),
                credentials: "include",
                body: JSON.stringify(payload),
            }
        );

        if (!response.ok) {
            throw new Error(
                `Transcription request failed with status ${response.status}`
            );
        }

        return { success: true };
    } catch (error) {
        console.error("Error starting transcription:", error);
        return { success: false, error: error.message };
    }
};

// Stop transcription API call
export const stopTranscription = async (device1, userId) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL}/stoptranscription_ey`,
            {
                method: "POST",
                headers: getHeaders(),
                credentials: "include",
                body: JSON.stringify({ device_1: device1, user_id: userId }),
            }
        );

        if (!response.ok) {
            throw new Error(
                `Transcription request failed with status ${response.status}`
            );
        }

        return { success: true };
    } catch (error) {
        console.error("Error stopping transcription:", error);
        return { success: false, error: error.message };
    }
};

// Start Video Recoding
export const startVideoRecording = async (callerID) => {
    try {

        const payload = { callerID: callerID }
        const response = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL}/start_video_recording`,
            {
                method: "POST",
                headers: getHeaders(),
                credentials: "include",
                body: JSON.stringify(payload),
            }
        );

        if (!response.ok) {
            throw new Error(
                `Video recording request failed with status ${response.status}`
            );
        }

        const json = await response.json();
        return { success: true, data: json };

    } catch (error) {
        console.error("Error in video recording:", error);
        return { success: false, error: error.message };
    }
};

// update Video Recording state
export const updateVideoRecordingState = async (callerID, resource_id, sid) => {
    try {

        const payload = { callerID: callerID, resource_id: resource_id, sid: sid }
        const response = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL}/update_video_recording_layout`,
            {
                method: "POST",
                headers: getHeaders(),
                credentials: "include",
                body: JSON.stringify(payload),
            }
        );

        if (!response.ok) {
            throw new Error(
                `Video recording request failed with status ${response.status}`
            );
        }

        return { success: true };

    } catch (error) {
        console.error("Error in video recording:", error);
        return { success: false, error: error.message };
    }
};

// Stop video Recording
export const stopVideoRecording = async (callerID) => {
    try {

        const payload = { callerID: callerID }
        const response = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL}/stop_video_recording`,
            {
                method: "POST",
                headers: getHeaders(),
                credentials: "include",
                body: JSON.stringify(payload),
            }
        );

        if (!response.ok) {
            throw new Error(
                `Video recording request failed with status ${response.status}`
            );
        }

        return { success: true };

    } catch (error) {
        console.error("Error in video recording:", error);
        return { success: false, error: error.message };
    }
};

// Update user profile API call
export const updateUserProfile = async (
    userId,
    preferredLang,
    translateBehaviour
) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/update_user/${userId}`,
            {
                method: "PUT",
                headers: getHeaders(),
                body: JSON.stringify({ preferredLang, translateBehaviour }),
                credentials: "include",
            }
        );

        if (!response.ok) {
            throw new Error(
                `Profile update request failed with status ${response.status}`
            );
        }

        return { success: true };
    } catch (error) {
        console.error("Error updating user profile:", error);
        return { success: false, error: error.message };
    }
};

// Update device profile API call
export const updateDeviceProfile = async (deviceId, preferredLang) => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_DEVICE_API_URL}/update?device_id=${deviceId}&preferredLang=${preferredLang}`,
            {
                method: "POST",
                headers: {
                    accept: "*/*",
                    portalscope: "b3NMWOVzfdRUjrW",
                    validatereq: generateRandomString(30),
                },
                credentials: "include",
            }
        );

        if (!response.ok) {
            throw new Error(
                `Profile update request failed with status ${response.status}`
            );
        }

        return { success: true };
    } catch (error) {
        console.error("Error updating device profile:", error);
        return { success: false, error: error.message };
    }
};
